/**
 * Evaluation API Client
 *
 * Handles all API calls for the evaluation feature:
 * - Start evaluation runs
 * - Poll run status
 * - Fetch results
 * - Submit human feedback
 */

import { fetchWithHeaders } from './fetchWithHeaders';

/* --------------------- */
/* ✅ Runtime configuration */
/* --------------------- */
let apiBase: string = import.meta.env.VITE_API_BASE_URL ?? '';

// ==================== Types ====================

export interface StartEvaluationRequest {
  dataset_name: string;
  negotiation_id: number;
  max_items?: number;
}

export interface EvaluationRunStatus {
  run_id: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  dataset_name: string;
  negotiation_id: number;
  total_items: number;
  processed_items: number;
  progress_percentage: number;
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

export interface EvaluationResult {
  item_id: string;
  question: string;
  response: string;
  reasoning?: string;
  references: Array<{ title?: string; source?: string; [key: string]: any }>;
  llm_judge_score?: number;
  llm_judge_comment?: string;  // LLM judge reasoning/comment
  trace_id?: string;
  human_feedback_score?: number;  // 1.0 = thumbs up, 0.0 = thumbs down
  human_feedback_notes?: string;
}

export interface HumanFeedbackRequest {
  thumbs_up: boolean;
  notes?: string;
}

// ==================== API Client ====================

export class EvaluationAPI {
  /** Called by your web component/store when Angular passes endpoint */
  static setBaseUrl(base: string) {
    apiBase = base;
  }

  /**
   * Start a new evaluation run
   */
  static async startRun(request: StartEvaluationRequest): Promise<{
    run_id: string;
    status: string;
    message: string;
  }> {
    const response = await fetchWithHeaders(`${apiBase}api/v1/evaluation/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start evaluation');
    }

    return response.json();
  }

  /**
   * Get evaluation run status (for polling)
   */
  static async getRunStatus(runId: string): Promise<EvaluationRunStatus> {
    const response = await fetchWithHeaders(`${apiBase}api/v1/evaluation/runs/${runId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch run status');
    }

    return response.json();
  }

  /**
   * Get evaluation results (call after status is COMPLETED)
   */
  static async getRunResults(runId: string): Promise<EvaluationResult[]> {
    console.log('[EvalAPI] Fetching results for run:', runId);
    const url = `${apiBase}api/v1/evaluation/runs/${runId}/results`;
    console.log('[EvalAPI] URL:', url);

    const response = await fetchWithHeaders(url);
    console.log('[EvalAPI] Response status:', response.status);

    if (!response.ok) {
      const error = await response.json();
      console.error('[EvalAPI] Error response:', error);
      throw new Error(error.detail || 'Failed to fetch results');
    }

    const data = await response.json();
    console.log('[EvalAPI] Response data:', data);
    return data;
  }

  /**
   * Submit human feedback (thumbs up/down + notes)
   */
  static async submitFeedback(
    traceId: string,
    feedback: HumanFeedbackRequest
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithHeaders(
      `${apiBase}api/v1/evaluation/results/${traceId}/feedback`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedback),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit feedback');
    }

    return response.json();
  }

  /**
   * List recent evaluation runs (for debugging)
   */
  static async listRecentRuns(limit: number = 10): Promise<EvaluationRunStatus[]> {
    const response = await fetchWithHeaders(`${apiBase}api/v1/evaluation/runs?limit=${limit}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to list runs');
    }

    return response.json();
  }
}
